import { secureUuid } from '../uuid';
import { useEffect, useRef, useState } from "react";
import { VariantControls } from "./VariantControls";
import {
    api,
    defaultPolicies,
    type CatalogGame,
    type Guest,
    type Room,
    type Policies,
    type SetupSetting,
} from "./api";
import { Avatar, Field, GameShield } from "./GuildhallParts";
import { generateGameName } from "./names";
import {
    enabledSettings,
    availableOptions,
    changeSettingValue,
    submittedSettingValues,
    validSetupValues,
    effectivePolicies,
    restoreSettingValues,
    randomSetupSelection,
} from "./setup";
import { VirtualList } from './VirtualList';
import { useBrowse } from './useBrowse';
import { BotTypeFields, botChoiceValid } from "./BotPicker";
import { botRequest, restoreBotChoice, selectableBots, type BotChoice, type BotRequest } from "./botTypes";

export function SetupFields({
    settings,
    values,
    change: update,
    mutableOnly = false,
    playerCount,
}: {
    settings: SetupSetting[];
    values: Record<string, unknown>;
    change: (key: string, value: unknown) => void;
    mutableOnly?: boolean;
    playerCount?: number;
}) {
    const [notice, setNotice] = useState("");
    const change = (key: string, value: unknown) => {
        const next = changeSettingValue(settings, values, key, value, playerCount);
        for (const [field, selection] of Object.entries(next.values)) update(field, selection);
        setNotice(next.reset ? "Some choices are unavailable with this content. The available default is shown; check your selections." : "");
    };
    return (
        <>
            {notice && <p role="status">{notice}</p>}
            {enabledSettings(settings, values, playerCount)
                .filter((s) => !mutableOnly || s.mutable)
                .map((s) => {
                    const value = values[s.key] ?? s.default;
                    const options = availableOptions(s, settings, values, playerCount);
                    return (
                        <div key={s.key} className="guild-setting">
                            <Field label={s.label} group={s.type === 'multiselect' || s.type === 'catalog'}>
                                {s.type === "boolean" ? (
                                    <input
                                        type="checkbox"
                                        checked={Boolean(value)}
                                        onChange={(e) =>
                                            change(s.key, e.target.checked)
                                        }
                                    />
                                ) : s.type === "integer" ? (
                                    <input
                                        type="number"
                                        min={s.minimum}
                                        max={s.maximum}
                                        value={Number(value)}
                                        onChange={(e) =>
                                            change(
                                                s.key,
                                                Number(e.target.value),
                                            )
                                        }
                                    />
                                ) : s.type === "text" ? (
                                    <input
                                        value={String(value ?? "")}
                                        maxLength={s.maximum ?? 200}
                                        onChange={(e) =>
                                            change(s.key, e.target.value)
                                        }
                                    />
                                ) : s.type === "enum" ? (
                                    <select
                                        value={String(value ?? "")}
                                        onChange={(e) =>
                                            change(s.key, e.target.value)
                                        }
                                    >
                                        {options.map((o) => (
                                            <option
                                                key={o.value ?? o.id}
                                                value={o.value ?? o.id}
                                            >
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <SetupOptionList label={s.label} options={options} selected={Array.isArray(value) ? value.map(String) : []} change={selected => change(s.key, selected)}/>

                                )}
                            </Field>
                            {s.type === 'multiselect' && s.randomize && <button type="button" className="button" disabled={options.length < (s.maxItems ?? 0)}
                                onClick={() => change(s.key, randomSetupSelection(options, s.maxItems!))}>Randomize {s.label}</button>}
                            {s.mutable && (
                                <p className="guild-hint">
                                    Changes take effect{" "}
                                    {s.effectiveBoundary === "nextTurn"
                                        ? "at the next turn"
                                        : s.effectiveBoundary === "nextRound"
                                          ? "at the next round"
                                          : "immediately"}
                                    , when the package permits the transition.
                                </p>
                            )}
                            {s.help && <p className="guild-muted">{s.help}</p>}
                            {options.find(o => (o.value ?? o.id) === value)?.preview?.map(group => (
                                <p className="guild-hint" key={group.label}><strong>{group.label}:</strong> {group.items.join(', ')}</p>
                            ))}
                            {(s.type === "multiselect" ||
                                s.type === "catalog") && (
                                <p className="guild-hint">
                                    Choose {s.minItems ?? 0}–
                                    {s.maxItems ?? options.length} items. Select the checkboxes to choose several.
                                </p>
                            )}
                        </div>
                    );
                })}
        </>
    );
}
export interface LaunchConfig {
    idempotencyKey: string;
    catalogId: string;
    variantId: string;
    name: string;
    seatPlan: "people" | "bots" | "host";
    claimSeat: boolean;
    ready: boolean;
    start: boolean;
    playerCount: number;
    setupValues: Record<string, unknown>;
    policies?: Policies;
    /** A non-default bot type for every bot seat; omitted for the house bot. */
    bot?: BotRequest;
}
export function GameSetup({
    catalog,
    room,
    guest,
    initial,
    busy,
    launch,
}: {
    catalog: CatalogGame[];
    room: Room;
    guest: Guest | null;
    initial?: string;
    busy: boolean;
    launch: (config: LaunchConfig) => Promise<unknown>;
}) {
    const [selected, setSelected] = useState(initial ?? "");
    const [search, setSearch] = useState("");
    const [name, setName] = useState(() =>
        generateGameName(
            "",
            Math.random,
            room.games.filter((g) => !g.archived).map((g) => g.name),
        ),
    );
    const [plan, setPlan] = useState<"people" | "bots" | "host">("people");
    const [variant, setVariant] = useState("");
    const [playerCount, setPlayerCount] = useState<number | null>(null);
    const [policies, setPolicies] = useState<Policies>(() =>
        effectivePolicies(room),
    );
    const [values, setValues] = useState<Record<string, unknown>>({});
    const [botChoice, setBotChoice] = useState<BotChoice | null>(null);
    const [fallback, setFallback] = useState("");
    const key = useRef(secureUuid());
    const submitted = useRef(false);
    const [game, setGame] = useState<CatalogGame | undefined>();
    const [pickError, setPickError] = useState<string | null>(null);
    const [picking, setPicking] = useState(false);
    const pickingRequest = useRef(0);
    const catalogPage = useBrowse<CatalogGame>('catalog', { q: search });
    const current =
        game?.variants.find((v) => v.id === variant) ??
        game?.variants.find(
            (v) => v.id === game.manifest?.defaultPreset && v.playable,
        ) ??
        game?.variants.find((v) => v.playable);
    const botTypes = selectableBots(current?.bots);
    const allowed = (c: CatalogGame) =>
        room.gamePolicy?.mode === "allowlist"
            ? room.gamePolicy.catalogIds.includes(c.id)
            : room.gamePolicy?.mode === "denylist"
              ? !room.gamePolicy.catalogIds.includes(c.id)
              : true;
    const pick = (c: CatalogGame) => {
        setGame(c);
        setSelected(c.id);
        setVariant(
            c.manifest?.defaultPreset ??
                c.variants.find((v) => v.playable)?.id ??
                "",
        );
        setPlan("people");
        setBotChoice(null);
        setPolicies(effectivePolicies(room));
        setPlayerCount(null);
        setValues(
            c.manifest?.presets.find((p) => p.id === c.manifest?.defaultPreset)
                ?.setupValues ?? {},
        );
        setFallback("");
        key.current = secureUuid();
        submitted.current = false;
        try {
            const saved = JSON.parse(
                localStorage.getItem(`guildhall-setup-${c.id}`) ?? "null",
            ) as {
                variant: string;
                plan: "people" | "bots" | "host";
                playerCount?: number;
                policies?: Policies;
                values: Record<string, unknown>;
                bot?: unknown;
            } | null;
            if (saved) {
                const retained = c.variants.find(
                    (v) => v.id === saved.variant && v.playable,
                );
                if (
                    retained &&
                    ["people", "bots", "host"].includes(saved.plan) &&
                    (saved.plan !== "bots" || retained.botsSupported)
                ) {
                    setVariant(saved.variant);
                    setPlan(saved.plan);
                    if (saved.bot) {
                        // Remembered only while the game still offers that exact type and version.
                        const restoredBot = restoreBotChoice(selectableBots(retained.bots), saved.bot);
                        setBotChoice(restoredBot?.choice ?? null);
                        if (!restoredBot || restoredBot.changed)
                            setFallback(
                                restoredBot
                                    ? "Some remembered bot settings no longer fit. Defaults are shown for those."
                                    : "Your remembered bot type is unavailable. The house bot is selected.",
                            );
                    }
                    const canRestoreCount = !!c.manifest?.independentPlayerCount || !!c.manifest?.playerCounts.some(n => !c.variants.some(v => v.playerCount === n));
                    const restoredCount = canRestoreCount && saved.playerCount && c.manifest?.playerCounts.includes(saved.playerCount) ? saved.playerCount : retained.playerCount;
                    if (canRestoreCount && saved.playerCount && c.manifest?.playerCounts.includes(saved.playerCount))
                        setPlayerCount(restoredCount);
                    else if (saved.playerCount && saved.playerCount !== retained.playerCount)
                        setFallback(
                            "Your remembered player count is unavailable. The preset count is shown.",
                        );
                    if (saved.policies)
                        setPolicies(
                            effectivePolicies(room, {
                                ...room.defaults,
                                ...saved.policies,
                            }),
                        );
                    const restored = restoreSettingValues(
                        c.manifest?.settings ?? [],
                        saved.values,
                        c.manifest?.presets.find((p) => p.id === saved.variant)
                            ?.setupValues,
                        restoredCount,
                    );
                    setValues(restored.values);
                    if (restored.fallback)
                        setFallback(
                            "Some remembered options no longer fit this manifest. Package defaults are shown.",
                        );
                } else
                    setFallback(
                        "Your remembered setup is unavailable. The current default is shown.",
                    );
            }
        } catch {
            setFallback("The current package defaults are shown.");
        }
    };
    const loadGame = async (id: string) => {
        const request = ++pickingRequest.current;
        setPicking(true); setPickError(null);
        try { const detail = await api<CatalogGame>(`/catalog/${encodeURIComponent(id)}`); if (pickingRequest.current === request) pick(detail); }
        catch (error) { if (pickingRequest.current === request) setPickError(error instanceof Error ? error.message : 'Game setup unavailable.'); }
        finally { if (pickingRequest.current === request) setPicking(false); }
    };
    useEffect(() => {
        if (initial) void loadGame(initial);
        return () => { ++pickingRequest.current; };
    }, []);
    const change = (work: () => void) => {
        if (submitted.current) {
            key.current = secureUuid();
            submitted.current = false;
        }
        work();
    };
    const effective = effectivePolicies(room, policies);
    const count = playerCount ?? current?.playerCount ?? 0;
    return (
        <div className="guild-picker">
            {!game ? (
                <>
                    <Field label="Search games">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Find your next quest…"
                        />
                    </Field>
                    {(pickError || catalogPage.error) && <p role="alert">{pickError ?? catalogPage.error}</p>}
                    {picking && <p role="status">Loading game options…</p>}
                    <VirtualList items={catalogPage.items} total={catalogPage.total} label="Choose a game" className="workspace-picker-list" itemKey={item => item.id} loading={catalogPage.loading} hasMore={!!catalogPage.nextCursor && !catalogPage.error} loadMore={catalogPage.loadMore} empty="No games match your search." render={item => <button type="button" className="guild-game-option workspace-picker-row" disabled={picking || !allowed(item) || !item.variants.some(v => v.playable)} onClick={() => void loadGame(item.id)}><GameShield game={item}/><span className="workspace-truncate">{item.title}</span><small>{allowed(item) ? [...new Set(item.variants.filter(v => v.playable).map(v => v.playerCount))].join(" / ") + " players" : "Restricted"}</small></button>}/>
                </>
            ) : (
                <form
                    className="guild-parchment"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!current) return;
                        const setupValues = submittedSettingValues(game.manifest?.settings ?? [], values, count);
                        submitted.current = true;
                        try {
                            localStorage.setItem(
                                `guildhall-setup-${game.id}`,
                                JSON.stringify({
                                    variant: current.id,
                                    plan,
                                    playerCount: count,
                                    policies: effective,
                                    values: setupValues,
                                    ...(botChoice ? { bot: botChoice } : {}),
                                }),
                            );
                        } catch {
                            /* Storage is optional. */
                        }
                        const bot = plan === "bots" ? botRequest(botTypes, botChoice) : undefined;
                        void launch({
                            idempotencyKey: key.current,
                            catalogId: game.id,
                            variantId: current.id,
                            name,
                            seatPlan: plan,
                            claimSeat: plan !== "host",
                            ready: plan !== "host",
                            start: true,
                            playerCount: count,
                            setupValues,
                            policies: effective,
                            ...(bot ? { bot } : {}),
                        });
                    }}
                >
                    <div className="guild-parch-head">
                        <GameShield game={game} />
                        <div>
                            <button
                                type="button"
                                className="guild-link"
                                onClick={() => { setSelected(""); setGame(undefined); }}
                            >
                                ← Quest rack
                            </button>
                            <h3>{game.title}</h3>
                            <p>
                                {count} players · Choose your party and set out.
                            </p>
                        </div>
                    </div>
                    <Field label="Game name">
                        <div className="guild-input-action">
                            <input
                                aria-label="Game name"
                                value={name}
                                onChange={(e) =>
                                    change(() => setName(e.target.value))
                                }
                                required
                                maxLength={80}
                            />
                            <button
                                type="button"
                                aria-label="Regenerate game name"
                                onClick={() =>
                                    change(() =>
                                        setName((n) =>
                                            generateGameName(
                                                n,
                                                Math.random,
                                                room.games
                                                    .filter((g) => !g.archived)
                                                    .map((g) => g.name),
                                            ),
                                        ),
                                    )
                                }
                            >
                                ↻
                            </button>
                        </div>
                    </Field>
                    {fallback && <p role="status">{fallback}</p>}
                    {!game.manifest?.independentPlayerCount && <VariantControls
                        variants={game.variants}
                        current={current}
                        onChange={(id) =>
                            change(() => {
                                setVariant(id);
                                setPlayerCount(null);
                                const next = game.variants.find((v) => v.id === id);
                                setBotChoice((prior) => (prior && selectableBots(next?.bots).some((t) => t.id === prior.type && t.version === prior.version) ? prior : null));
                                setValues(
                                    game.manifest?.presets.find(
                                        (p) => p.id === id,
                                    )?.setupValues ?? {},
                                );
                            })
                        }
                    />}
                    {(game.manifest?.independentPlayerCount || game.manifest?.playerCounts.some(
                        (n) => !game.variants.some((v) => v.playerCount === n),
                    )) && (
                        <Field label="Player count">
                            <select
                                value={count}
                                onChange={(e) =>
                                    change(() =>
                                        setPlayerCount(Number(e.target.value)),
                                    )
                                }
                            >
                                {game.manifest.playerCounts.map((n) => (
                                    <option key={n} value={n}>
                                        {n} players
                                    </option>
                                ))}
                            </select>
                        </Field>
                    )}
                    {game.manifest?.independentPlayerCount && <SetupFields
                        settings={game.manifest.settings}
                        values={values}
                        playerCount={count}
                        change={(k, v) => change(() => setValues(prev => ({ ...prev, [k]: v })))}
                    />}
                    <fieldset className="guild-seat-plan">
                        <legend>Your party</legend>
                        <div className="guild-segment">
                            <button
                                type="button"
                                aria-pressed={plan === "people"}
                                onClick={() => change(() => setPlan("people"))}
                            >
                                Play with people
                            </button>
                            <button
                                type="button"
                                aria-pressed={plan === "bots"}
                                disabled={!current?.botsSupported}
                                onClick={() => change(() => setPlan("bots"))}
                            >
                                Play with bots
                            </button>
                            <button
                                type="button"
                                aria-pressed={plan === "host"}
                                onClick={() => change(() => setPlan("host"))}
                            >
                                Host without playing
                            </button>
                        </div>
                        <div className="guild-seat-preview">
                            {Array.from({ length: count }, (_, i) => (
                                <div key={i}>
                                    <Avatar
                                        profile={
                                            i === 0 && plan !== "host"
                                                ? guest?.profile
                                                : undefined
                                        }
                                        name={
                                            i === 0 && plan !== "host"
                                                ? (guest?.name ?? "You")
                                                : plan === "bots"
                                                  ? botChoice
                                                    ? `${botTypes.find((t) => t.id === botChoice.type)?.label ?? "Bot"} bot`
                                                    : "House bot"
                                                  : "Open seat"
                                        }
                                        bot={i > 0 && plan === "bots"}
                                        open={
                                            plan === "host" ||
                                            (i > 0 && plan === "people")
                                        }
                                    />
                                    <small>
                                        {i === 0 && plan !== "host"
                                            ? "You · ready"
                                            : plan === "bots"
                                              ? botChoice
                                                ? `Bot · ${botTypes.find((t) => t.id === botChoice.type)?.label ?? "ready"}`
                                                : "Bot · ready"
                                              : "Open seat"}
                                    </small>
                                </div>
                            ))}
                        </div>
                        {plan === "bots" && botTypes.length > 1 && (
                            <BotTypeFields types={botTypes} choice={botChoice} onChange={(choice) => change(() => setBotChoice(choice))} />
                        )}
                    </fieldset>
                    <details>
                        <summary>Advanced configuration</summary>
                        <fieldset>
                            <legend>Table settings</legend>
                            {Object.entries(defaultPolicies).map(([k]) => {
                                const key = k as keyof Policies;
                                const fixed = room.constraints?.[key]?.value;
                                return (
                                    <label className="guild-toggle" key={k}>
                                        <span>
                                            {
                                                (
                                                    {
                                                        undoEnabled:
                                                            "Undo against bots",
                                                        humanReplacement:
                                                            "People can sub in",
                                                        botReplacement:
                                                            "Bots can sub in",
                                                        seatSharing:
                                                            "Explicit seat sharing",
                                                        liveSpectating:
                                                            "Live watching",
                                                        completedPerspectiveReview:
                                                            "Review any seat after completion",
                                                    } as Record<string, string>
                                                )[k]
                                            }
                                            {fixed !== undefined
                                                ? " · room enforced"
                                                : ""}
                                        </span>
                                        <input
                                            type="checkbox"
                                            disabled={fixed !== undefined}
                                            checked={
                                                fixed ?? Boolean(policies[key])
                                            }
                                            onChange={(e) =>
                                                change(() =>
                                                    setPolicies((p) => ({
                                                        ...p,
                                                        [key]: e.target.checked,
                                                    })),
                                                )
                                            }
                                        />
                                    </label>
                                );
                            })}
                            <Field label="Undo depth (blank is unlimited)">
                                <input
                                    type="number"
                                    min={
                                        room.constraints?.undoDepth?.minimum ??
                                        0
                                    }
                                    max={
                                        room.constraints?.undoDepth?.maximum ??
                                        10000
                                    }
                                    value={policies.undoDepth ?? ""}
                                    onChange={(e) =>
                                        change(() =>
                                            setPolicies((p) => ({
                                                ...p,
                                                undoDepth: e.target.value
                                                    ? Number(e.target.value)
                                                    : null,
                                            })),
                                        )
                                    }
                                />
                            </Field>
                        </fieldset>
                        {!game.manifest?.independentPlayerCount && <SetupFields
                            settings={game.manifest?.settings ?? []}
                            values={values}
                            playerCount={count}
                            change={(k, v) =>
                                change(() =>
                                    setValues((prev) => ({ ...prev, [k]: v })),
                                )
                            }
                        />}
                        {!game.manifest?.settings.length && (
                            <p>This preset has no optional rule settings.</p>
                        )}
                    </details>
                    <p className="guild-hint">
                        Room charter:{" "}
                        {effective.undoEnabled
                            ? "Undo against bots"
                            : "Undo off"}{" "}
                        ·{" "}
                        {effective.humanReplacement
                            ? "People can sub in"
                            : "Human replacement off"}{" "}
                        ·{" "}
                        {effective.botReplacement
                            ? "Bots can sub in"
                            : "Bot replacement off"}{" "}
                        ·{" "}
                        {effective.completedPerspectiveReview
                            ? "Review any seat after completion"
                            : "Own-seat review only"}
                    </p>
                    <div className="guild-setup-footer">
                        <p>
                            {plan === "bots" || (count === 1 && plan !== "host")
                                ? "Starts with your seat ready. Nothing to type."
                                : "Your seat is ready. Other people must claim and ready their own seats."}
                        </p>
                        <button
                            className="guild-btn primary"
                            disabled={busy || !current || !allowed(game) || !validSetupValues(game.manifest?.settings ?? [], values, count) || (plan === "bots" && !botChoiceValid(botTypes, botChoice))}
                        >
                            {busy
                                ? "Preparing your quest…"
                                : plan === "bots" ||
                                    (count === 1 && plan !== "host")
                                  ? "Set out · Start game →"
                                  : "Create table →"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

function SetupOptionList({label,options,selected,change}: {label:string;options:{value?:string;id?:string;label:string}[];selected:string[];change:(values:string[])=>void}) {
    const [query,setQuery]=useState("");
    const rows=options.map(option=>({...option,id:option.value ?? option.id ?? ""})).filter(option=>option.label.toLowerCase().includes(query.toLowerCase()));
    return <div className="workspace-setup-options"><input type="search" aria-label={`Search ${label.toLowerCase()} options`} value={query} onChange={event=>setQuery(event.target.value)} placeholder="Filter options…"/><p className="guild-hint">{selected.length} selected</p><VirtualList items={rows} label={`${label} options`} rowHeight={32} className="workspace-account-list" itemKey={option=>option.id} empty="No matching options." render={option=><label className="workspace-picker-row"><input type="checkbox" checked={selected.includes(option.id)} onChange={event=>change(event.target.checked?[...selected,option.id]:selected.filter(id=>id!==option.id))}/><span className="workspace-truncate">{option.label}</span></label>}/></div>;
}
